/**
 * Generic Subcategory Detection System
 *
 * Provides a reusable framework for detecting subcategories within expense categories
 * based on keyword matching and confidence scoring.
 */

/**
 * Index signature allows `string[] | undefined` so callers passing
 * a `Partial`-style keyword map (language files where some
 * subcategories aren't populated) can plug in without casts. The
 * detector loop skips any subcategory whose keyword list is
 * absent.
 */
export interface SubcategoryKeywords {
  [subcategory: string]: string[] | undefined;
}

/**
 * Detection config. Icons are NOT part of this — the detection
 * engine only cares about which subcategory keys are valid. Each
 * platform (web / mobile) keeps its own subcategory → icon mapping
 * outside of core, since the icon component types differ (web's
 * `lucide-react` vs mobile's `lucide-react-native`).
 */
export interface SubcategoryConfig {
  keywords: SubcategoryKeywords;
  /** Subcategory keys this category recognises. Used both as a
      whitelist when matching and to drive `getValidSubcategories`. */
  validSubcategories: string[];
  contextKeywords?: Record<string, string[]>;
  minConfidenceThreshold?: number;
  subcategoryPriority?: string[]; // Higher priority subcategories when multiple matches
}

export interface SubcategoryResult {
  subcategory: string;
  confidence: number;
  matchedKeywords: string[];
  /**
   * Best keyword score before the in-category tie haircut. Ranking
   * categories against each other must use this, not `confidence`:
   * a category whose own subcategories tie is still a confident
   * category hit (it just doesn't know which subcategory).
   */
  matchScore?: number;
}

/**
 * Confidence reported for a tie. Sits under the scheduler's
 * `KEYWORD_TRUST_THRESHOLD` (0.8) so the chip still shows but the
 * cloud tier is asked to break the tie.
 */
export const KEYWORD_TIE_CONFIDENCE = 0.65;
/**
 * Scores are 0.8 or 0.6 plus a 0.1 length bonus, so 0.9 - 0.85 lands a
 * rounding error above 0.05; compare with slack.
 */
export const KEYWORD_TIE_MARGIN = 0.05 + 1e-9;

export interface SubcategoryMatch {
  subcategory: string;
  keyword: string;
  score: number;
}

/** Regex chars that need escaping in a literal pattern. */
const REGEX_META = /[.*+?^${}()|[\]\\]/g;

/**
 * Compiled-pattern cache for `unicodeWordMatch`. Each prediction iterates
 * the full keyword set (hundreds–thousands of keywords across the active
 * locale + brands), and compiling a Unicode-property regex per keyword is
 * expensive on slower JS engines — on Hermes / an older iPhone it added up
 * to ~2.7s of synchronous, JS-thread-blocking work per call. Keywords are a
 * fixed, bounded set, so memoizing the compiled pattern by keyword string
 * compiles each one exactly once; every subsequent match is a cache hit.
 * The regexes carry no `g` flag, so `.test()` is stateless and safe to reuse.
 */
const patternCache = new Map<string, RegExp>();

/**
 * Unicode-aware "whole word" match. Returns true iff `keyword` appears
 * in `text` flanked by non-letter, non-digit, non-underscore characters
 * (or string boundaries). JS's native `\b` only knows the ASCII word
 * class, so it treats every accented letter as a "non-word char" and
 * fails for keywords like 'meliá', 'barceló', 'café', or any input
 * containing them. This helper uses lookbehind/lookahead against the
 * Unicode `\p{L}` and `\p{N}` classes (`u` flag) so the boundaries
 * behave the way users expect across all the locales we support.
 */
function unicodeWordMatch(keyword: string, text: string): boolean {
  let pattern = patternCache.get(keyword);
  if (!pattern) {
    const escaped = keyword.replace(REGEX_META, '\\$&');
    pattern = new RegExp(
      `(?<![\\p{L}\\p{N}_])${escaped}(?![\\p{L}\\p{N}_])`,
      'iu'
    );
    patternCache.set(keyword, pattern);
  }
  return pattern.test(text);
}

/**
 * Generic subcategory detection function
 */
export function detectSubcategory(
  description: string,
  config: SubcategoryConfig
): SubcategoryResult {
  const normalizedText = description.toLowerCase().trim();
  const minThreshold = config.minConfidenceThreshold || 0.5;

  if (!normalizedText) {
    return { subcategory: 'default', confidence: 0, matchedKeywords: [] };
  }

  const matches: SubcategoryMatch[] = [];

  // Check each subcategory's keywords. The map's index signature
  // allows `string[] | undefined` (Partial-style maps from language
  // files); skip subcategories that aren't populated.
  for (const [subcategory, keywords] of Object.entries(config.keywords)) {
    if (!keywords) continue;
    for (const keyword of keywords) {
      const normalizedKeyword = keyword.toLowerCase();

      // Unicode-aware word boundary. JS's `\b` only treats [A-Za-z0-9_] as
      // word chars, which breaks for any keyword ending in or surrounded
      // by accented letters: `\bmeliá\b` against "meliá madrid" fails
      // because 'á' isn't a word char, so the 'á'→' ' transition isn't
      // a word boundary by ASCII rules. Lookbehind/lookahead against the
      // Unicode Letter/Number classes fixes this without false positives.
      const hasWordMatch = unicodeWordMatch(normalizedKeyword, normalizedText);

      // Also check for substring match for compound words
      const hasSubstringMatch = normalizedText.includes(normalizedKeyword);

      if (hasWordMatch || (hasSubstringMatch && normalizedKeyword.length > 5)) {
        let score = hasWordMatch ? 0.8 : 0.6; // Higher score for word boundary match

        // Higher score for longer, more specific keywords
        if (keyword.length >= 6) {
          score += 0.1;
        }

        matches.push({ subcategory, keyword, score });
      }
    }
  }

  // Check context keywords if provided
  if (config.contextKeywords) {
    for (const [subcategory, contextKeywords] of Object.entries(
      config.contextKeywords
    )) {
      for (const keyword of contextKeywords) {
        const normalizedKeyword = keyword.toLowerCase();
        if (unicodeWordMatch(normalizedKeyword, normalizedText)) {
          // Context keywords get a higher score for specific subcategories
          const score = 0.9;
          matches.push({ subcategory, keyword, score });
        }
      }
    }
  }

  if (matches.length === 0) {
    return { subcategory: 'default', confidence: 0, matchedKeywords: [] };
  }

  // Group matches by subcategory and calculate best score per subcategory
  const subcategoryScores: Record<
    string,
    { score: number; keywords: string[] }
  > = {};

  for (const match of matches) {
    if (!subcategoryScores[match.subcategory]) {
      subcategoryScores[match.subcategory] = { score: 0, keywords: [] };
    }

    // Take the highest score for this subcategory and collect keywords
    subcategoryScores[match.subcategory].score = Math.max(
      subcategoryScores[match.subcategory].score,
      match.score
    );
    subcategoryScores[match.subcategory].keywords.push(match.keyword);
  }

  // Find the subcategory with the highest score, considering priority
  let bestSubcategory = 'default';
  let bestScore = 0;
  let bestKeywords: string[] = [];

  // First, find the highest scoring subcategory
  for (const [subcategory, data] of Object.entries(subcategoryScores)) {
    if (data.score > bestScore) {
      bestScore = data.score;
      bestSubcategory = subcategory;
      bestKeywords = data.keywords;
    }
  }

  // If priority is defined and we have multiple matches with very similar scores, use priority as tie-breaker
  if (config.subcategoryPriority && Object.keys(subcategoryScores).length > 1) {
    // Check if there are other subcategories with similar scores (within 0.1)
    const similarScores = Object.entries(subcategoryScores).filter(
      ([_, data]) => Math.abs(data.score - bestScore) <= 0.1
    );

    if (similarScores.length > 1) {
      // Use priority order as tie-breaker for similar scores
      for (const prioritySubcategory of config.subcategoryPriority) {
        const priorityMatch = similarScores.find(
          ([sub]) => sub === prioritySubcategory
        );
        if (priorityMatch) {
          bestSubcategory = priorityMatch[0];
          bestScore = priorityMatch[1].score;
          bestKeywords = priorityMatch[1].keywords;
          break;
        }
      }
    }
  }

  // Tie-haircut: when 2+ subcategories score within 0.05 of the winner,
  // the keyword tier is genuinely ambiguous (e.g. typing "Walmart" matches
  // food/groceries and shopping/groceries equally). Lower the returned
  // confidence so the cloud LLM upgrade tier — which gates on confidence
  // — is invited to disambiguate. The keyword answer still ships in the
  // live tier, just marked as "soft" via the lower number.
  const matchScore = bestScore;
  const tiedCount = Object.values(subcategoryScores).filter(
    data => bestScore - data.score <= KEYWORD_TIE_MARGIN
  ).length;
  if (tiedCount > 1) {
    bestScore = Math.min(bestScore, KEYWORD_TIE_CONFIDENCE);
  }

  // Apply minimum confidence threshold
  if (bestScore < minThreshold) {
    return { subcategory: 'default', confidence: 0, matchedKeywords: [] };
  }

  return {
    subcategory: bestSubcategory,
    confidence: bestScore,
    matchedKeywords: bestKeywords,
    matchScore,
  };
}

/**
 * Get valid subcategories (excluding 'default'). Just returns the
 * config's `validSubcategories` array — kept as a helper for
 * callers that read it through this entrypoint.
 */
export function getValidSubcategories(config: SubcategoryConfig): string[] {
  return config.validSubcategories.filter(key => key !== 'default');
}
